import {
    TYPESENSE_PROJECTS_COLLECTION_NAME,
    TYPESENSE_USERS_COLLECTION_NAME,
    TYPESENSE_ORGANIZATIONS_COLLECTION_NAME,
    TYPESENSE_USER_EMAILS_COLLECTION_NAME,
    TYPESENSE_PARTS_COLLECTION_NAME,
    TYPESENSE_PARTS_VECTORIZED_COLLECTION_NAME,
} from "@buildwithflux/constants";
import type {CollectionCreateSchema} from "typesense/lib/Typesense/Collections";

// Defines schemas for Typesense
/**
 * Note we use int64 for timestamp fields (created_at, updated_at, etc.) because
 * JavaScript stores timestamps in milliseconds since epoch (~1.77e12 in 2026),
 * which exceeds the int32 range (max ~2.15e9). Other count fields use int32
 * since their values are well within range.
 *
 * The MAX_SAFE_INTEGER in JavaScript is 2^53 - 1, so int64 values are safely
 * representable in JS
 *
 * NOTE: that we need to explicitly set index: false to unindex a field, and so anytime a new
 * field is added, make sure to review that
 */
export const projectsSchema = {
    name: TYPESENSE_PROJECTS_COLLECTION_NAME,
    // Defining fields for `ISearchDocumentData`
    fields: [
        // Indexable fields (searchable attributes in Algolia)
        // Sortable so queries can use uid as a unique final sort tie-breaker
        {name: "uid", type: "string", index: true, sort: true},
        {name: "name", type: "string", index: true},
        {name: "slug", type: "string", index: true},
        {name: "description", type: "string", index: true},

        // Mixin for counts
        // Setting them to be indexable because we will use them to do rank and sort
        {name: "fork_count", type: "int32", index: true},
        {name: "star_count", type: "int32", index: true},
        {name: "comment_count", type: "int32", index: true},
        {name: "usage_count", type: "int32", index: true},
        {name: "copy_count", type: "int32", index: true},

        // IOrganizationOwnerMixin
        {name: "owner_uid", type: "string", index: true},
        {name: "organization_owner_uid", type: "string", optional: true, index: true},
        {name: "organization_member_permission_type", type: "int32", optional: true, index: false},

        // IEnterpriseOwnerMixin
        {name: "enterprise_owner_uid", type: "string", optional: true, index: false},
        {name: "enterprise_member_permission_type", type: "int32", optional: true, index: false},
        {name: "enterprise_owner_handle", type: "string", optional: true, index: false},
        {name: "organization_owner_is_enterprise_default", type: "bool", optional: true, index: false},

        // ITimestampMixin
        // Using int64 because JS timestamps in milliseconds exceed int32 range
        {name: "created_at", type: "int64", index: true, sort: true},
        {name: "updated_at", type: "int64", index: true, sort: true},

        // IThumbnailMixin
        {name: "preview_image", type: "string", optional: true, index: false},
        {name: "three_d_model_key", type: "string", optional: true, index: false},
        {name: "three_d_model_z_is_up", type: "bool", optional: true, index: false},

        // IAccessPermissionMixin
        {name: "userAccessList", type: "string[]", index: true, facet: false},
        {name: "anyoneCanView", type: "bool", index: true},

        // IPublishedMixin & ITestDataMixin
        {name: "belongs_to_part_uid", type: "string", index: false},
        {name: "test_data", type: "bool", index: true},

        // IDocumentOwnershipFields — short ID for canonical URLs
        {name: "short_id", type: "string", optional: true, index: true},

        // Rest of fields
        {name: "contributor_uids", type: "string[]", index: false},
        {name: "has_custom_name", type: "bool", optional: true, index: false},
        {name: "owner_handle", type: "string", index: true},
        {name: "archived", type: "bool", index: true},
        {name: "is_published", type: "bool", index: true},
        // Indexed: profile-page filters query `is_organization_owned:!=true` (see packages/search/src/filters/profile.ts)
        {name: "is_organization_owned", type: "bool", index: true},
        {name: "partUid", type: "string", index: false},

        {name: "seo", type: "string", index: true, optional: true},
        {name: "seo_detail", type: "string", index: true, optional: true, facet: true},

        // Profile card enrichment counts (populated by windowed recount, not real-time sync)
        {name: "element_count", type: "int32", optional: true, index: false},
        {name: "net_count", type: "int32", optional: true, index: false},
        {name: "symbol_count", type: "int32", optional: true, index: false},
        {name: "layout_count", type: "int32", optional: true, index: false},
        {name: "footprint_count", type: "int32", optional: true, index: false},
        {name: "copilot_thread_count", type: "int32", optional: true, index: false},
        // Using int64 because JS timestamps in milliseconds exceed int32 range
        {name: "last_message_at", type: "int64", optional: true, index: false},
        {name: "knowledge_suggestion_count", type: "int32", optional: true, index: false},
        {name: "average_price", type: "float", optional: true, index: false},
    ],
    default_sorting_field: "star_count",
} satisfies CollectionCreateSchema;

export const usersSchema = {
    name: TYPESENSE_USERS_COLLECTION_NAME,
    fields: [
        {
            name: "handle",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },

        {
            name: "uid",
            type: "string",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },

        {
            name: "star_count",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "documents_count",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "parts_count",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "follower_count",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "following_count",
            type: "int32",
            facet: false,
            index: false,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },

        {
            name: "test_data",
            type: "bool",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },

        // IUserImageMixin
        {
            name: "picture",
            type: "string",
            facet: false,
            index: false,
            infix: false,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "custom_picture_key",
            type: "string",
            facet: false,
            index: false,
            infix: false,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },

        // IAccessPermissionMixin
        {
            name: "userAccessList",
            type: "string[]",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "anyoneCanView",
            type: "bool",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },

        // Using int64 because JS timestamps in milliseconds exceed int32 range
        {
            name: "last_active_at",
            type: "int64",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "archived",
            type: "bool",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "organization_member_uids",
            type: "string[]",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "organization_owner_uids",
            type: "string[]",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "seo",
            type: "string",
            facet: true,
            index: true,
            infix: false,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "seo_detail",
            type: "string",
            facet: true,
            index: true,
            infix: false,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "sla",
            type: "string",
            facet: false,
            index: true,
            infix: false,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },

        // ITimestampMixin
        // Using int64 because JS timestamps in milliseconds exceed int32 range
        {
            name: "created_at",
            type: "int64",
            facet: false,
            index: true,
            infix: false,
            optional: true,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "updated_at",
            type: "int64",
            facet: false,
            index: true,
            infix: false,
            optional: true,
            sort: true,
            stem: false,
            store: true,
        },

        // IUserSocialMixin
        {
            name: "website_url",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "full_name",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "twitter_handle",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "github_handle",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "tag_line",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: true,
            store: true,
        },
    ],
    token_separators: ["-"],
} satisfies CollectionCreateSchema;

export const userEmailsSchema = {
    name: TYPESENSE_USER_EMAILS_COLLECTION_NAME,
    fields: [
        {name: "handle", type: "string", index: false},
        {name: "email", type: "string", index: true},
        {name: "emailDomain", type: "string", index: true},
        {name: "userAccessList", type: "string[]", index: true},
        {name: "anyoneCanView", type: "bool", index: true},
    ],
    token_separators: ["-", "_", "+", "@", "."],
} satisfies CollectionCreateSchema;

export const organizationsSchema = {
    name: TYPESENSE_ORGANIZATIONS_COLLECTION_NAME,
    fields: [
        {name: "uid", type: "string", index: true},
        {name: "enterpriseUid", type: "string", index: false, optional: true},

        {name: "handle", type: "string", infix: true, index: true},
        {name: "displayName", type: "string", infix: true, index: true},
        {name: "bioContent", type: "string", index: true, infix: true, optional: true},
        {name: "customPictureKey", type: "string", index: false, optional: true},
        {name: "picture", type: "string", index: false, optional: true},
        {name: "websiteUrl", type: "string", index: true, infix: true, optional: true},
        {name: "githubHandle", type: "string", index: true, infix: true, optional: true},
        {name: "twitterHandle", type: "string", index: true, infix: true, optional: true},

        {name: "visibility", type: "string", index: true},

        {name: "test_data", type: "bool", index: true, sort: true, optional: true},
        {name: "isTest", type: "bool", index: true, sort: true},

        {name: "partsCount", type: "int32", index: true, sort: true, optional: true},
        {name: "documentsCount", type: "int32", index: true, sort: true, optional: true},
        {name: "follower_count", type: "int32", index: true, sort: true, optional: true},
        {name: "following_count", type: "int32", index: true, sort: true, optional: true},

        // IAccessPermissionMixin
        {name: "userAccessList", type: "string[]", index: true},
        {name: "anyoneCanView", type: "bool", index: true, sort: true},

        // Using int64 because JS timestamps in milliseconds exceed int32 range
        {name: "createdAt", type: "int64", index: true, sort: true},
        {name: "updatedAt", type: "int64", index: true, sort: true},

        {name: "seo", type: "string", index: true, optional: true, facet: true},
        {name: "seo_detail", type: "string", index: true, optional: true, facet: true},

        {name: "archived", type: "bool", index: true, sort: true, optional: true},

        // Normalized field names for union search compatibility with users collection
        // Using int64 because JS timestamps in milliseconds exceed int32 range
        {name: "created_at", type: "int64", index: true, sort: true, optional: true},
        {name: "updated_at", type: "int64", index: true, sort: true, optional: true},
        {name: "last_active_at", type: "int64", index: true, sort: true, optional: true},
    ],
} satisfies CollectionCreateSchema;

export const partsSchema = {
    name: TYPESENSE_PARTS_COLLECTION_NAME,
    fields: [
        // IAccessPermissionMixin
        {name: "userAccessList", type: "string[]", index: true},
        {name: "anyoneCanView", type: "bool", index: true},

        // IStarCountMixin
        {name: "star_count", type: "int32", index: true},

        // ITestDataMixin
        {name: "test_data", type: "bool", index: true, optional: false},

        // IOrganizationOwnerMixin
        {name: "owner_uid", type: "string", index: true},
        {name: "organization_owner_uid", type: "string", optional: true, index: true},
        {name: "organization_member_permission_type", type: "int32", optional: true, index: false},

        // IEnterpriseOwnerMixin
        {name: "enterprise_owner_uid", type: "string", optional: true, index: false},
        {name: "enterprise_member_permission_type", type: "int32", optional: true, index: false},
        {name: "enterprise_owner_handle", type: "string", optional: true, index: false},
        {name: "organization_owner_is_enterprise_default", type: "bool", optional: true, index: false},

        {name: "uid", type: "string", index: true, facet: true},
        {name: "name", type: "string", index: true},
        {name: "description", type: "string", index: true},

        // Relative canonical URL of the part's source project (e.g. /handle/slug~shortId),
        // collapsed into a single stored value. Absent for parts with no source document.
        {name: "project_url", type: "string", index: false, optional: true},

        {name: "has_custom_name", type: "bool", optional: true, index: false},
        {name: "nameSuffixes", type: "string[]", optional: true, index: true},

        {name: "preview_image", type: "string", optional: true, index: false},
        {name: "three_d_model_key", type: "string", optional: true, index: false},
        {name: "three_d_model_z_is_up", type: "bool", optional: true, index: false},

        // Using int64 because JS timestamps in milliseconds exceed int32 range
        {name: "created_at", type: "int64", index: true, sort: true, optional: true},
        {name: "updated_at", type: "int64", index: true, sort: true, optional: true},

        {name: "defaultValueSynonyms", type: "string[]", index: true, optional: true},
        {name: "properties", type: "object[]", index: false, optional: true},
        {name: "properties.value", type: "string[]", index: true, optional: true},
        {name: "properties.unit", type: "string[]", index: true, optional: true},
        {name: "part_type", type: "string", index: true, facet: true, optional: true},
        {name: "part_sub_type", type: "string", index: true, facet: true, optional: true},
        // Nested facet fields below require `index: true` on the parent object
        // and `enable_nested_fields: true` on the collection.
        {name: "part_type_specific_values", type: "object", index: true, optional: true},
        {name: "part_type_specific_values.resistance", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.tolerance", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.powerRating", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.resistorType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.capacitance", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.voltageRating", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.capacitorType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.inductance", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.currentRating", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.inductorShielding", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.inductorType", type: "string", index: true, facet: true, optional: true},
        {
            name: "part_type_specific_values.drainToSourceVoltage",
            type: "string",
            index: true,
            facet: true,
            optional: true,
        },
        {
            name: "part_type_specific_values.continuousDrainCurrent",
            type: "string",
            index: true,
            facet: true,
            optional: true,
        },
        {name: "part_type_specific_values.transistorType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.reverseVoltage", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.forwardVoltage", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.diodeType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.operatingVoltage", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.powerConsumption", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.frequency", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.logicFunction", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.icType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.gender", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.pitch", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.positions", type: "int32", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.rows", type: "int32", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.connectorType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.sensorType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.interfaceType", type: "string[]", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.displayType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.displaySize", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.resolution", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.touchType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.actuatorType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.frameSize", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.fastenerType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.threadSize", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.length", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.headType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.driveType", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.material", type: "string", index: true, facet: true, optional: true},
        {name: "part_type_specific_values.mechanicalType", type: "string", index: true, facet: true, optional: true},
        {name: "mount", type: "string", index: true, facet: true, optional: true},
        {name: "package_case_code", type: "string", index: true, facet: true, optional: true},
        {name: "manufacturer", type: "string", index: true, facet: true, optional: true},
        {name: "terminal_count", type: "int32", index: true, facet: true, optional: true},
        {name: "terminal_names", type: "string[]", index: true, optional: true},
        {name: "compliance", type: "string[]", index: true, optional: true, facet: true},
        {name: "archived", type: "bool", index: true, optional: true},
        {name: "version", type: "string", index: false, optional: true},

        {name: "seo", type: "string", index: true, optional: true},
        {name: "seo_detail", type: "string", index: true, optional: true, facet: true},

        {name: "use_count", type: "int32", index: false, optional: true},
        {name: "has_behaviour", type: "bool", index: true, optional: true},
        {name: "has_datasheet", type: "bool", index: true, optional: true},
        {name: "has_machine_datasheet", type: "bool", index: true, optional: true},
        {name: "has_mpn", type: "bool", index: true, facet: true, optional: true},
        {name: "mpn", type: "string", index: true, optional: true},
        {name: "has_sim_model", type: "bool", index: true, optional: true},
        {name: "layout_count", type: "int32", index: true, facet: true, optional: true},
        {name: "sub_layout_count", type: "int32", index: false, optional: true},
        {name: "footprint_count", type: "int32", index: true, optional: true, facet: true},
        {name: "three_d_model_count", type: "int32", index: true, optional: true},
        {name: "owner_name", type: "string", index: true, optional: true},
        {name: "owner_handle", type: "string", index: true, facet: true, optional: true},
        {name: "partVersionData", type: "object", index: false, optional: true},
        {name: "contentScore", type: "float", index: true},
        {name: "lowest_price", type: "float", index: true, optional: true},
        {name: "available_at_distributors", type: "string[]", index: true, optional: true, facet: true},
        {name: "reviewIssueSummary", type: "object", index: false, optional: true},
    ],
    default_sorting_field: "contentScore",
    enable_nested_fields: true,
} satisfies CollectionCreateSchema;

export const partsVectorizedSchema = {
    ...partsSchema,
    name: TYPESENSE_PARTS_VECTORIZED_COLLECTION_NAME,
    fields: [...partsSchema.fields],
} satisfies CollectionCreateSchema;
